import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  DatePicker,
  Upload,
  Button,
  Typography,
  Row,
  Col,
  Input,
  GetProp,
  UploadProps,
  Select,
} from 'antd';
import {
  DeleteOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { StyledFormItem, UnifiedImage } from '../../Facility/FacilityFormModal';
import styled from 'styled-components';
import {
  DateFieldKey,
  EventFormState,
  UserType,
} from '../../../../types/types';
import { COLORS, TEXT_SIZES } from '../../../../utils/constants';
import {
  dateFieldGroups,
  startOfDayFields,
} from '../../../../utils/eventUtils';
import LocationPicker, { LocationValue } from '../../Maps/LocationPicker';
import Modal from '../../../common/Modal';
import { searchUsersRequest } from '../../../../api/requests/users';
import { EntityStatus, UserRole } from '../../../../enums/enums';
import { RcFile } from 'antd/es/upload';
import { useSnackbar } from '../../../../contexts/SnackbarContext/SnackbarProvider';
import { debounce } from '../../../../utils/utils';

const { Text, Title } = Typography;

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

interface Props {
  formState: EventFormState;
  setFormState: React.Dispatch<React.SetStateAction<EventFormState>>;
  errors: Record<string, string>;
  images: UnifiedImage[];
  setImages: React.Dispatch<React.SetStateAction<UnifiedImage[]>>;
  onRemoveImage: (index: number) => void;
  setTouched: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const todayDate = dayjs();

export const getBase64 = (img: FileType, callback: (url: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

const { Option } = Select;

const Step1BasicInfo: React.FC<Props> = ({
  formState,
  setFormState,
  errors,
  images,
  setImages,
  onRemoveImage,
  setTouched,
}) => {
  const [datePickerField, setDatePickerField] = useState<DateFieldKey | null>(
    null,
  );
  const [locationPickerOpen, setLocationPickerOpen] = useState<boolean>(false);
  const [eventOrganisers, setEventOrganisers] = useState<UserType[]>([]);
  const snackbar = useSnackbar();
  const [searchText, setSearchText] = useState<string>('');
  const [isLoadingOrganisers, setIsLoadingOrganisers] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMoreOrganisers, setHasMoreOrganisers] = useState<boolean>(true);

  const fetchEventOrganisers = async (
    searchQuery: string,
    page: number,
    append: boolean,
  ) => {
    try {
      setIsLoadingOrganisers(true);
      const organisersResponse = await searchUsersRequest({
        userRole: UserRole.EventOrganizer,
        EntityStatus: EntityStatus.Active,
        SearchQuery: searchQuery || undefined,
        PageNumber: page,
        PageSize: 20, // Fetch 20 records per page
      });
      if (organisersResponse && organisersResponse.isSuccess) {
        const newOrganisers = organisersResponse.value?.rows || [];
        if (append) {
          setEventOrganisers((prev) => [...prev, ...newOrganisers]);
        } else {
          setEventOrganisers(newOrganisers);
        }
        // Check if there are more records
        const totalRecords = organisersResponse.value?.totalCount || 0;
        setHasMoreOrganisers(page * 20 < totalRecords);
      }
    } catch (error) {
      console.error('Error fetching event organisers:', error);
    } finally {
      setIsLoadingOrganisers(false);
    }
  };

  // Debounced search function
  const debouncedSearchOrganisers = useMemo(
    () =>
      debounce((searchQuery: string) => {
        setCurrentPage(0);
        fetchEventOrganisers(searchQuery, 0, false);
      }, 500),
    [],
  );

  useEffect(() => {
    fetchEventOrganisers('', 0, false);
  }, []);

  // Handle search input change
  const handleOrganiserSearch = (value: string) => {
    setSearchText(value);
    debouncedSearchOrganisers(value);
  };

  // Handle scroll to load more
  const handleOrganiserScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (
      target.scrollTop + target.clientHeight >= target.scrollHeight - 10 &&
      !isLoadingOrganisers &&
      hasMoreOrganisers
    ) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchEventOrganisers(searchText, nextPage, true);
    }
  };

  const eventOrganiserOptions = useMemo(() => {
    return eventOrganisers.map((organiser) => ({
      label: organiser.fullName,
      value: organiser.userId,
    }));
  }, [eventOrganisers]);

  const handleDateChange = (field: DateFieldKey, date: Dayjs | null) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    if (!date) {
      setFormState((prev) => ({ ...prev, [field]: undefined }));
      return;
    }

    const normalized = startOfDayFields.includes(field)
      ? date.startOf('day')
      : date.endOf('day');

    setFormState((prev) => ({ ...prev, [field]: normalized }));
  };

  const getMinDate = (field: DateFieldKey) => {
    switch (field) {
      case 'registrationStartDate':
        return todayDate;
      case 'registrationEndDate':
        return formState.registrationStartDate ?? todayDate;
      case 'eventStartDate':
        return formState.registrationEndDate
          ? formState.registrationEndDate.add(1, 'day')
          : todayDate;
      case 'eventEndDate':
        return formState.eventStartDate ?? todayDate;
      default:
        return todayDate;
    }
  };

  const disabledDate = (current: Dayjs | null) => {
    if (!datePickerField || !current) return false;
    const min = getMinDate(datePickerField);
    return current.isBefore(min, 'day');
  };

  const beforeUpload = (file: RcFile) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const allowedExtensions = ['jpg', 'jpeg', 'png'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidType = allowedTypes.includes(file.type);
    const isValidExtension = fileExtension
      ? allowedExtensions.includes(fileExtension)
      : false;
    if (!isValidType || !isValidExtension) {
      snackbar.error('Only jpeg, png and jpg formats allowed!');
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto-upload but allow manual processing
  };

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    // Filter out files that failed validation (status 'error' or undefined means beforeUpload returned false)
    const validFiles = info.fileList.filter(
      (f) => f.status !== 'error' && f.originFileObj,
    );

    if (validFiles.length === 0) {
      return; // No valid files to process
    }

    const files = validFiles.map((f) => f.originFileObj as FileType);

    Promise.all(
      files.map(
        (file) =>
          new Promise<string>((resolve) => {
            getBase64(file, (url) => resolve(url));
          }),
      ),
    ).then((urls) => {
      const newImages: UnifiedImage[] = urls.map((url, idx) => ({
        url,
        file: files[idx],
        isPrimary: false,
        isNew: true,
      }));
      setImages((prev) => [...prev.filter((img) => !img.isNew), ...newImages]);
      setTouched((prev) => ({ ...prev, eventImage: true }));
      setFormState((prev) => ({
        ...prev,
        eventImage: urls[0],
      }));
    });
  };

  const handleLocationChange = (location: LocationValue) => {
    const addressString = location.address
      ? `${location.address.street}, ${location.address.city}, ${location.address.state} ${location.address.pincode}`
      : '';
    setFormState((prev) => ({
      ...prev,
      eventAddress: addressString,
      eventLat: location.lat,
      eventLng: location.lng,
    }));
    setTouched((prev) => ({ ...prev, eventAddress: true }));
    setLocationPickerOpen(false);
  };

  const renderDatePickerField = (label: string, fieldKey: DateFieldKey) => (
    <Col span={12} key={fieldKey}>
      <StyledFormItem
        label={
          <span>
            {label} <Text type="danger">*</Text>
          </span>
        }
        validateStatus={errors[fieldKey] ? 'error' : ''}
        help={errors[fieldKey]}
      >
        <DatePicker
          style={{ width: '100%', height: 40 }}
          value={formState[fieldKey] ?? null}
          format="DD MMM YYYY"
          onChange={(date) => handleDateChange(fieldKey, date)}
          onOpenChange={(open) => setDatePickerField(open ? fieldKey : null)}
          disabledDate={disabledDate}
        />
      </StyledFormItem>
    </Col>
  );

  return (
    <Form layout="vertical">
      <StyledFormItem
        label={
          <span>
            Event Name <Text type="danger">*</Text>
          </span>
        }
        validateStatus={errors.eventName ? 'error' : ''}
        help={errors.eventName}
      >
        <StyledInput
          value={formState.eventName ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setTouched((prev) => ({ ...prev, eventName: true }));
            setFormState((p) => ({ ...p, eventName: e.target.value }));
          }}
        />
      </StyledFormItem>

      <StyledFormItem
        label={
          <span>
            Description <Text type="danger">*</Text>
          </span>
        }
        validateStatus={errors.description ? 'error' : ''}
        help={errors.description}
      >
        <StyledInput.TextArea
          rows={4}
          value={formState.description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setTouched((prev) => ({ ...prev, description: true }));
            setFormState((p) => ({ ...p, description: e.target.value }));
          }}
        />
      </StyledFormItem>

      <EventOrganiserSelectItem
        label="Event Organiser"
        name="eventOrganizerUserId"
      >
        <Select
          placeholder="Select organiser"
          size="large"
          value={formState.eventOrganizerUserId}
          onChange={(value) =>
            setFormState((prev) => ({ ...prev, eventOrganizerUserId: value }))
          }
          showSearch
          filterOption={false}
          onSearch={handleOrganiserSearch}
          onPopupScroll={handleOrganiserScroll}
          loading={isLoadingOrganisers}
          notFoundContent={
            isLoadingOrganisers ? 'Loading...' : 'No organisers found'
          }
        >
          {eventOrganiserOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </EventOrganiserSelectItem>

      <StyledFormItem
        label={
          <span>
            Upload Image <Text type="danger">*</Text>
          </span>
        }
        validateStatus={errors.eventImage ? 'error' : ''}
        help={errors.eventImage}
      >
        {images && images.length > 0 ? (
          <Row gutter={16}>
            {images.map((img, index) => (
              <Col key={index}>
                <ImageContainer>
                  <ImagePreview src={img.url} alt="event" />
                  <DeleteButton
                    type="text"
                    size="small"
                    icon={<DeleteIcon />}
                    onClick={() => {
                      setTouched((prev) => ({ ...prev, eventImage: true }));
                      onRemoveImage(index);
                    }}
                  />
                </ImageContainer>
              </Col>
            ))}
          </Row>
        ) : (
          <Upload
            maxCount={1}
            showUploadList={false}
            beforeUpload={beforeUpload}
            onChange={handleUploadChange}
            accept="image/jpeg,image/png,image/jpg"
          >
            <UploadButton>
              <CameraIcon />
              Upload Image
            </UploadButton>
          </Upload>
        )}

        <SupportedFormatsText>
          Supported formats: JPG, JPEG, PNG
        </SupportedFormatsText>
      </StyledFormItem>

      <EventSection>
        <Title level={5}>
          <CalendarOutlined /> Event Schedule
        </Title>
        <Row gutter={16}>
          {dateFieldGroups
            .flat()
            .map((field) => renderDatePickerField(field.label, field.key))}
        </Row>
      </EventSection>

      <EventSection>
        <TitleWithButton>
          <Title level={5}>
            <EnvironmentOutlined /> Event Location
          </Title>
        </TitleWithButton>

        <StyledFormItem
          label={
            <>
              <span>
                Event Address
                <Text type="danger"> *</Text>
              </span>
              <Slash>/</Slash>
              <LocationPickerButton
                type="primary"
                icon={<EnvironmentOutlined />}
                onClick={() => setLocationPickerOpen(true)}
                size="small"
              >
                Pick Location
              </LocationPickerButton>
            </>
          }
          validateStatus={errors.eventAddress ? 'error' : ''}
          help={errors.eventAddress}
        >
          <StyledInput.TextArea
            rows={3}
            value={formState.eventAddress}
            placeholder="Enter address or use location picker"
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setTouched((prev) => ({ ...prev, eventAddress: true }));
              setFormState((p) => ({ ...p, eventAddress: e.target.value }));
            }}
          />
        </StyledFormItem>
      </EventSection>

      <EventSection>
        <Title level={5}>
          <FileTextOutlined /> Registration
        </Title>
        <StyledFormItem
          label={
            <span>
              Registration Fee <Text type="danger">*</Text>
            </span>
          }
          validateStatus={errors.registrationFee ? 'error' : ''}
          help={errors.registrationFee}
        >
          <StyledInput
            type="number"
            value={formState.registrationFee ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTouched((prev) => ({ ...prev, registrationFee: true }));
              const cleaned = e.target.value.replace(/[^0-9]/g, '');
              setFormState((p) => ({
                ...p,
                registrationFee: cleaned ? Number(cleaned) : null,
              }));
            }}
          />
        </StyledFormItem>
      </EventSection>

      <Modal
        title="Pick Event Location"
        open={locationPickerOpen}
        onCancel={() => setLocationPickerOpen(false)}
        footer={null}
        width={1200}
      >
        <LocationPicker
          value={
            formState.eventLat && formState.eventLng
              ? {
                  lat: formState.eventLat,
                  lng: formState.eventLng,
                  address: undefined,
                }
              : undefined
          }
          onLocationChange={handleLocationChange}
          height="800px"
        />
      </Modal>
    </Form>
  );
};

const StyledInput = styled(Input)`
  height: 43px;
`;

const ImageContainer = styled.div`
  position: 'relative';
`;
const ImagePreview = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: 4px;
  left: 4px;
  background: ${COLORS.white.primary};
  border-radius: 50%;
`;

const DeleteIcon = styled(DeleteOutlined)`
  color: red;
`;

const UploadButton = styled(Button)`
  height: 100px;
  flex-direction: column;
`;

const CameraIcon = styled(CameraOutlined)`
  font-size: 20px;
`;

const EventSection = styled.div`
  margin-top: 24px;
`;

const TitleWithButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const LocationPickerButton = styled(Button)`
  display: flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 6px;
  gap: 4px;
`;

const Slash = styled.span`
  margin: 0 8px;
  font-size: ${TEXT_SIZES.MD}px;
  color: ${COLORS.grey.primary};
`;

const EventOrganiserSelectItem = styled(StyledFormItem)`
  max-width: 300px;
`;

const SupportedFormatsText = styled.p`
  margin: 4px 0px;
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.grey.secondary};
`;

export default Step1BasicInfo;
